import { LoadingButton } from "@mui/lab";
import { Button, Grid, TextField } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { TokenReply, TokenRequest } from "../../api/models";
import SaveIcon from '@mui/icons-material/Save';
import { getService } from "../../app/api";
import { AuthService, TenantService, UserService } from "../../api/services";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./Login.module.css";
import { useDispatch } from "react-redux";
import { setUser } from '../../app/appSlice';
import session from "../../app/Session";
import { useTranslation } from "react-i18next";
import FacebookIcon from '@mui/icons-material/Facebook';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import EmailIcon from '@mui/icons-material/Email';
import { Password } from "@mui/icons-material";
import RegisterView from "./Register";

export default function Login() {
    let [panel, setPanel] = React.useState<'login'|'register'>('login');

    return(
    <div style={{ padding: 32 ,position:'relative' }} >     
        <div style={{display: panel == 'login' ? 'block': 'none'}}>
            <LoginView onRegister={()=>setPanel('register')} />
        </div>
        <div style={{display: panel == 'register' ? 'block': 'none'}}>
            <RegisterView onRegister={()=> setPanel('login')}/>
        </div>
    </div>
    )
}

interface LoginViewProps {
    onRegister?: () => void;
}

function LoginView(props:LoginViewProps) {
    const { t, i18n } = useTranslation();
    let [item, setItem] = React.useState<TokenRequest>({ grandType: 'password', } as TokenRequest);
    let [loading, setLoading] = React.useState(false);
    let [authCode, setAuthCode] = React.useState<string | undefined>();
    let [useDeviceCode, setUseDeviceCode] = React.useState(false);
    let [deviceCode, setDeviceCode] = React.useState<string | null>("");
    let [error, setError] = React.useState<string | null>(null);
    let intervalRef = useRef(0);
    let intervalTimeRef = useRef(0);
    let navigate = useNavigate();
    let dispatch = useDispatch();
    let location = useLocation();

    useEffect(() => {
        if (authCode) {
            handleAuthCode()
        } else if (location.hash) {
            let params = new URLSearchParams(location.hash.substring(1));
            let code = params.get('code');
            if (code) {
                authCode = code;
                setAuthCode(code);                
            }
            if( params.get('error')) {
                setError(params.get('error'));
            }
        }
    }, [authCode]);

    useEffect(()=>{
        return ()=>{
            if(intervalRef.current != 0){
                window.clearInterval(intervalRef.current);
                intervalRef.current = 0;
                intervalTimeRef.current = 0;
            }
        }
    },[])

    const handleAuthCode = async () => {
        const srv = getService(AuthService);
        setLoading(true);
        try {
            let reply = await srv.getToken({ grandType: 'code', code: authCode });
            if (reply) {
                if (reply.access_token) {
                    window.localStorage.setItem('token', reply.access_token);
                    await loadUser();
                }
                if (reply.refresh_token) {
                    window.localStorage.setItem('refresh-token', reply.refresh_token);
                }
            }
        } finally {
            setAuthCode(undefined);
            setLoading(false);
            navigate('/', { replace: true });           
        }
    }

    const handleLogin = async () => {
        const srv = getService(AuthService);
        setLoading(true);
        try {
            let reply = await srv.getToken(item);
            await onTokenReply(reply);
        }catch (e:any) {
            if(e.statusText)
                setError(e.statusText);
            else{
                setError('An error occurred, please try again later');
            }
        }
         finally {
            setLoading(false);
        }
    }

    const loadUser = async () => {
        const srv = getService(UserService);
        let reply = await srv.getSessionUser();
        session.login(reply);

        let tenant = await getService(TenantService).getSessionTenant();
        session.setTenant(tenant);
        
        dispatch(setUser(reply));
    }

    const checkDeviceCode =async ()=>{
        try{
            let token = await getService(AuthService).getTokenFromDeviceCode({ code: deviceCode });
            if(token && token.access_token){
                clearInterval(intervalRef.current);
                onTokenReply(token);                                
            }
        }catch(e){
            intervalTimeRef.current += 1500;
            if(intervalTimeRef.current >= 600000 ){
                window.clearInterval(intervalRef.current);
                intervalRef.current = 0;
                intervalTimeRef.current = 0;
            }
        }        
    }

    const onGetDeviceCode = async () => {
        try {
            let reply = await getService(AuthService).createDeviceCode();
            if(reply.code){
                deviceCode = reply.code;
                setDeviceCode(reply.code)
                setUseDeviceCode(true);
                if(intervalRef.current != 0){
                    window.clearInterval(intervalRef.current);
                }
                let internal = window.setInterval(checkDeviceCode, 1500);
                intervalRef.current = internal;
                intervalTimeRef.current = 0;
                let time = window
            }else{
                console.log("Device code not returned")
            }
        } catch (e) {
            console.error(e);
        }
    }

    async function onTokenReply(reply: TokenReply) {
        if (reply) {
            if (reply.access_token) {
                window.localStorage.setItem('token', reply.access_token);
                await loadUser();
            }
            if (reply.refresh_token) {
                window.localStorage.setItem('refresh-token', reply.refresh_token);
            }
            navigate('/', { replace: true });
        }
    }

    function setValue(field: keyof TokenRequest, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        setItem(prev => {
            return {
                ...prev,
                [field]: e.target.value
            } as TokenRequest
        });
    }


    return (                   
        <Grid  container spacing={1} className={styles.root} >
            { error && (
                <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>                        
                </Grid>)
            }
            <Grid item xs={12}>
                <TextField required                       
                    className={styles.textfield} 
                    onChange={e => setValue('username', e)} 
                    value={item.username} 
                    variant="filled"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                            <EmailIcon />
                            </InputAdornment>
                        ),
                        }}
                        />
            </Grid>
            <Grid item xs={12}>
                <TextField required 
                    type={t("password")} 
                    className={styles.textfield}                          
                    onChange={e => setValue('password', e)} 
                    value={item.password} 
                    variant="filled"
                    onKeyDown={e=> e.key == 'Enter' && handleLogin()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                            <Password />
                            </InputAdornment>
                        ),
                        }}
                        />
            </Grid>
            <Grid item xs={12} style={{ marginTop: '1rem' }}>
                <LoadingButton
                    variant="outlined"
                    loading={loading}
                    loadingPosition="start"                        
                    startIcon={<SaveIcon />}
                    onClick={handleLogin}>
                    {t('login_email')}
                </LoadingButton>
            </Grid>
            <Grid item xs={12}>
                <Button style={{ marginTop: '1rm' }}                            
                        variant="outlined"
                        startIcon={<FacebookIcon />}
                        href={`${process.env.REACT_APP_API_URL}/login-facebook`} >
                        {t('login_facebook')}
                </Button>
            </Grid>
            {/* <Grid item xs={12}>
                <Button style={{ marginTop: '1rm' }}
                        
                        variant="outlined"
                        startIcon={<GoogleIcon />}
                        href={`${process.env.REACT_APP_API_URL}/login-google`} >
                        {t('login_google')}
                </Button>
            </Grid> */}
            <Grid item xs={12}>
                <Button style={{ marginTop: '1rm' }}                        
                    variant="outlined"
                    onClick={onGetDeviceCode} >
                    {t('login_code')}
                </Button>
            </Grid>
            {useDeviceCode && (
                <Grid item xs={12}>
                    <TextField
                        className={styles.textfield}
                        style={{fontSize:'large'}}
                        label={t("Device Code")}                          
                        variant="outlined"
                        aria-aria-readonly={true}
                        value={deviceCode} />
                </Grid>
            )}
            <Grid item xs={12}>
                <Button style={{ marginTop: '1rm' }}                        
                    variant="outlined"
                    onClick={props.onRegister} >
                    {t('register')}
                </Button>
            </Grid>
        </Grid>                 
    )
}